import type { Seconds } from '@lemonade-hq/ts-helpers';
import { isNativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import { Flex, themeVars, useThemeImage, useViewportSize } from '@lemonade-hq/uniclient-theme';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { wizardBackground } from './WizardBackgroundImage.css';

interface WizardBackgroundImageProps {
  readonly backgroundImage: {
    readonly lg: string;
    readonly sm: string;
  };
  readonly lastComponentRef: React.RefObject<HTMLDivElement>;
  readonly animationDelay: number;
  readonly historyVisible?: boolean;
}

const FADE_IN_DURATION: Seconds = 1;
const FADE_OUT_DURATION: Seconds = 0.5;

const GRADIENT_BACKGROUND_WHITE_OFFSET = 5;
const GRADIENT_BACKGROUND_TRANSPARENT_OFFSET = 60;

export const WizardBackgroundImage: React.FC<WizardBackgroundImageProps> = ({
  backgroundImage,
  lastComponentRef,
  animationDelay,
  historyVisible = false,
}) => {
  const { isMobileViewport } = useViewportSize();
  const [componentBottomPosition, setComponentBottomPosition] = useState<number | null>(null);
  const imageUrl = useThemeImage(isMobileViewport ? backgroundImage.sm : backgroundImage.lg);

  useEffect(() => {
    if (!lastComponentRef.current) return;

    const rect = lastComponentRef.current.getBoundingClientRect();
    setComponentBottomPosition(rect.bottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastComponentRef.current]);

  const gradientBackground = useMemo(() => {
    if (componentBottomPosition == null || !isMobileViewport) return '';

    return `linear-gradient(to bottom, ${themeVars.color.backgroundPrimary} ${Math.round(componentBottomPosition) - GRADIENT_BACKGROUND_WHITE_OFFSET}px, transparent ${
      Math.round(componentBottomPosition) + GRADIENT_BACKGROUND_TRANSPARENT_OFFSET
    }px),`;
  }, [componentBottomPosition, isMobileViewport]);

  const nativeApp = isNativeApp();

  return (
    <Flex
      animate={{
        opacity: 1,
        transition: { delay: animationDelay / 1000, duration: FADE_IN_DURATION },
      }}
      as={motion.div}
      exit={{ opacity: 0, transition: { duration: FADE_OUT_DURATION } }}
      initial={{ opacity: 0 }}
    >
      <Flex
        className={wizardBackground({ nativeApp, historyVisible })}
        style={{
          backgroundImage: `${gradientBackground}url(${imageUrl})`,
        }}
      />
    </Flex>
  );
};
